<template>
    <div v-resize="onResize" v-scroll="onScroll">
        <div class="ds-page-layout-layover" :class="{ 'ds-page-layout-layover--show' : loading }"></div>
        <slot name="cookie-bar"></slot>
        <div class="ds-page-layout" :class="`ds-page-layout--${pageLayoutTheme}`">
            <header
                class="ds-page-layout__header"
                :class="[
                    { 'ds-page-layout__header--hidden': isHeaderHidden },
                    { 'ds-page-layout__header--fixed': isHeaderPositionedFixed },
                    { 'ds-page-layout__header--transition': hasHeaderTransition }
                ]">
                <slot name="header" :header-theme="pageLayoutTheme"></slot>
            </header>
            <main class="ds-page-layout__content">
                <slot name="content"></slot>
            </main>
            <footer class="ds-page-layout__footer">
                <slot name="footer"></slot>
            </footer>
            <div class="ds-page-layout__overlay">
                <slot name="overlay"></slot>
            </div>
        </div>
        <div v-once v-html="noscriptHtml"></div>
    </div>
</template>

<script>
import { SCREEN_BREAKPOINT_MEDIUM, SCREEN_BREAKPOINT_LARGE } from '../../foundation/tokens/exported-variables.js';
import { scroll, resize } from '../../utils/directives';
import { theme } from '../../utils/mixins';

// scss variables in exported-variables
const HEADER_HEIGHT = 72;

/**
 * Page default layout.
 * represents a container for building a page, contains main page areas
 *
 * @example
 *
 * ```js
 * import { DsPageLayout } from '@/layout'
 *
 * export default {
 *     components: { DsPageLayout },
 *     template: `
 *         <DsPageLayout>
 *            <template #cookie-bar>
 *                ...
 *            </template>
 *            <template #header>
 *               ...
 *            </template>
 *            <template #content>
 *               ...
 *            </template>
 *            <template #footer>
 *               ...
 *            </template>
 *            <template #overlay>
 *               ...
 *            </template>
 *        </DsPageLayout>
 *     `
 * };
 * ```
 */
export default {
    name: 'DsPageLayout',
    directives: { resize, scroll },
    mixins: [theme],
    props: {
        preventTransition: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        loading: true,
        screen: 'small',
        isHeaderHidden: false,
        isHeaderPositionedFixed: false,
        hasHeaderTransition: false,
        freezeTransition: false
    }),
    computed: {
        pageLayoutTheme () {
            // force light theme when header appears while scrolling up
            if (this.isHeaderPositionedFixed && !this.isHeaderHidden) {
                return this.$options.theme.light;
            }
            return this.theme;
        }
    },
    provide () {
    // provide reactive property
        const screen = {};
        Object.defineProperty(screen, 'name', {
            enumerable: true,
            get: () => this.screen
        });
        return {
            screen,
            updateHeaderFreezeTransition: this.updateHeaderFreezeTransition
        };
    },
    created () {
    // The page layover is hiding the content of the page until the "screen" variable has been calculated,
    // this is solving the issue of page layout jump from small screen to the current one
    // when Javascript is disabled, there is no need to show the layover as the screen will always have the default value
        this.noscriptHtml = '<noscript><style>.ds-page-layout-layover--show { display: none !important; }</style></noscript>';
    },
    mounted () {
        const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
        this.screen = this.getScreen(windowWidth);
        this.loading = false;
    },
    methods: {
        getScreen (width) {
            switch (true) {
            case width >= SCREEN_BREAKPOINT_LARGE:
                return 'large';
            case width >= SCREEN_BREAKPOINT_MEDIUM:
                return 'medium';
            case width < SCREEN_BREAKPOINT_MEDIUM:
            default:
                return 'small';
            }
        },
        onResize (windowWidth) {
            this.screen = this.getScreen(windowWidth);
        },
        onScroll ({ currentPosition, lastPosition }) {
            // On page scroll down "below header" -> header element hides
            // On page scroll up -> header element shows
            const isBelowHeader = currentPosition > HEADER_HEIGHT;

            // top of the page reached
            if (currentPosition < 0) {
                return;
            }
            // prevent transition
            if (this.preventTransition) {
                this.$emit('transition-prevented');
                return;
            }
            // freeze transition
            if (this.freezeTransition) {
                this.handleFreezeTransition();
                if (currentPosition === 0) {
                    this.handleScrollUp(true);
                }
                return;
            }
            // on scroll
            if (lastPosition > currentPosition) {
                this.handleScrollUp(currentPosition === 0);
            } else if (isBelowHeader) {
                this.handleScrollDownBelowHeader(currentPosition === HEADER_HEIGHT + 1);
            }
        },
        handleScrollUp (isTopOfPage) {
            this.hasHeaderTransition = !isTopOfPage;
            if (isTopOfPage) {
                this.isHeaderPositionedFixed = false;
            }
            this.isHeaderHidden = false;
        },
        handleScrollDownBelowHeader (hasTransition) {
            if (this.isHeaderHidden) {
                return;
            }
            this.isHeaderHidden = true;
            this.isHeaderPositionedFixed = true;
            this.hasHeaderTransition = hasTransition;
        },
        handleFreezeTransition () {
            this.isHeaderHidden = false;
            this.isHeaderPositionedFixed = true;
            this.hasHeaderTransition = true;
        },
        updateHeaderFreezeTransition (value) {
            this.freezeTransition = value;
        }
    }
};
</script>

<style lang="scss" scoped>
@import '../../foundation/tokens/variables';

.ds-page-layout-layover {
    background-color: $color-white;
    display: none;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 3;

    &--show {
        display: block;
    }
}

.ds-page-layout {
    background-color: transparent;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 100vh;

    &--dark { background-color: $color-black; }
    &--light { background-color: $color-white; }
    &--green { background-color: $color-green; }
    &--grey { background-color: $color-grey-025; }

    &__header {
        background-color: transparent;
        height: $height-header;
        position: absolute;
        transform: translateY(0);
        width: 100%;
        z-index: $z-index-1;

        &--fixed {
            // force light theme
            background-color: $color-white;
            position: fixed;
        }

        &--transition {
            transition: 0.15s transform ease-out;
        }

        &--hidden {
            transform: translateY(-100%);
        }
    }

    &__content {
        display: flex;
        flex: 1;
        height: 100%;
        margin-top: $height-header;
        width: 100%;
        z-index: $z-index-0;
    }

    &__footer {
        height: 100%;
        min-height: $spacing-12*10;
    }

    &__overlay {
        z-index: 1;
    }
}
</style>
